import {
  animateTo,
  getAnimation,
  parseDuration,
  setDefaultAnimation,
  stopAnimations
} from '../../common/animation-common.js';
import { classMap } from 'lit/directives/class-map.js';
import { html } from 'lit';
import { monitor } from '../../common/monitor.js';
import { property, query } from 'lit/decorators.js';
import { waitForEvent } from '../../events/events.js';
import componentStyles from '../../styles/component.styles.js';
import styles from './preview.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';
import type WebmodulePopup from '../popup/popup.js';

/**
 * @summary A hover-triggered preview popup with keyboard accessibility. Shows a panel with title and
 * scrollable content when the user hovers or focuses the trigger element.
 *
 * @slot - The trigger element (typically a button) that activates the preview.
 * @slot title - The title displayed in the preview header.
 * @slot content - The main content displayed in the preview body with scrollable support.
 *
 * @csspart base - The popup container.
 * @csspart panel - The preview panel containing title and content.
 * @csspart header - The title section of the preview.
 * @csspart body - The scrollable content section.
 *
 * @cssproperty --max-width - Maximum width of the preview panel (default: 400px).
 * @cssproperty --max-height - Maximum height of the scrollable content (default: 200px).
 * @cssproperty --show-delay - The amount of time to wait before showing the preview when hovering (default: 150ms).
 * @cssproperty --hide-delay - The amount of time to wait before hiding the preview when hovering (default: 0ms).
 *
 * @event webmodule-show - Emitted when the preview is shown.
 * @event webmodule-after-show - Emitted after the preview show animation completes.
 * @event webmodule-hide - Emitted when the preview starts to hide.
 * @event webmodule-after-hide - Emitted after the preview hide animation completes.
 *
 * @tag webmodule-preview
 */
export default class WebmodulePreview extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('.preview__panel') panel: HTMLElement;
  @query('webmodule-popup') popup: WebmodulePopup;

  /** Controls the preferred placement of the preview popup. */
  @property() placement:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end' = 'top';

  /** Disables the preview popup. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Controls whether the preview is currently visible. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Use fixed positioning strategy to prevent clipping in scrollable containers. */
  @property({ type: Boolean }) hoist = false;

  private hoverTimeout: number;
  private closeWatcher: CloseWatcher | null;
  private distance = 8;
  private skidding = 0;
  private cachedShowDelay: number | null = null;
  private cachedHideDelay: number | null = null;

  constructor() {
    super();
    this.addEventListener('blur', this.handleBlur, true);
    this.addEventListener('focus', this.handleFocus, true);
    this.addEventListener('mouseover', this.handleMouseOver);
    this.addEventListener('mouseout', this.handleMouseOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closeWatcher?.destroy();
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  }

  firstUpdated() {
    this.panel.hidden = !this.open;

    if (this.open) {
      this.popup.active = true;
      this.popup.reposition();
    }
  }

  @monitor('open', { delayMonitorUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.open) {
      if (this.disabled) {
        // Reset open back to false if disabled to prevent inconsistent state
        this.open = false;
        return;
      }

      // Show
      this.emit('webmodule-show');
      if ('CloseWatcher' in window) {
        this.closeWatcher?.destroy();
        this.closeWatcher = new CloseWatcher();
        this.closeWatcher.onclose = () => {
          this.hide();
        };
      } else {
        document.addEventListener('keydown', this.handleDocumentKeyDown);
      }

      await stopAnimations(this.panel);
      this.panel.hidden = false;
      this.popup.active = true;
      const { keyframes, options } = getAnimation(this, 'preview.show');
      await animateTo(this.popup.popup, keyframes, options);
      this.popup.reposition();

      this.emit('webmodule-after-show');
    } else {
      // Hide
      this.emit('webmodule-hide');
      this.closeWatcher?.destroy();
      document.removeEventListener('keydown', this.handleDocumentKeyDown);

      await stopAnimations(this.panel);
      const { keyframes, options } = getAnimation(this, 'preview.hide');
      await animateTo(this.popup.popup, keyframes, options);
      this.popup.active = false;
      this.panel.hidden = true;

      this.emit('webmodule-after-hide');
    }
  }

  @monitor(['placement', 'hoist'])
  async handleOptionsChange() {
    if (this.hasUpdated) {
      await this.updateComplete;
      this.popup.reposition();
    }
  }

  @monitor('disabled')
  handleDisabledChange() {
    if (this.disabled && this.open) {
      this.hide();
    }
  }

  /** Shows the preview. */
  async show() {
    if (this.open || this.disabled) {
      return undefined;
    }

    this.open = true;
    return waitForEvent(this, 'webmodule-after-show');
  }

  /** Hides the preview. */
  async hide() {
    if (!this.open) {
      return undefined;
    }

    this.open = false;
    return waitForEvent(this, 'webmodule-after-hide');
  }

  render() {
    return html`
      <webmodule-popup
        part="base"
        exportparts="popup:base__popup"
        class=${classMap({
          preview: true,
          'preview--open': this.open
        })}
        placement=${this.placement}
        distance=${this.distance}
        skidding=${this.skidding}
        strategy=${this.hoist ? 'fixed' : 'absolute'}
        flip
        shift
        hover-bridge
      >
        <slot slot="anchor"></slot>
        <div part="panel" class="preview__panel">
          <div part="header" class="preview__header">
            <slot name="title"></slot>
          </div>
          <webmodule-fade part="body" class="preview__body" orientation="vertical">
            <slot name="content"></slot>
          </webmodule-fade>
        </div>
      </webmodule-popup>
    `;
  }

  /**
   * Checks if the target is inside this component (light DOM, shadow DOM, or nested shadow DOM).
   * Walks up the shadow host chain to handle nested shadow DOMs.
   */
  private isTargetInNestedShadowDOM(target: Node): boolean {
    // Check if the target is in the light DOM
    if (this.contains(target)) {
      return true;
    }

    // Check if the target is in the shadow DOM
    if (this.shadowRoot?.contains(target)) {
      return true;
    }

    // Check if the target is inside a nested shadow DOM by walking up the shadow host chain
    let currentNode: Node | null = target;

    while (currentNode) {
      const root: Node = currentNode.getRootNode();
      if (root instanceof ShadowRoot) {
        const host = root.host;
        if (this.contains(host) || this.shadowRoot?.contains(host)) {
          return true;
        }
        currentNode = host;
      } else {
        break;
      }
    }
    return false;
  }

  private handleBlur = (event: FocusEvent) => {
    // Only hide if focus is moving outside this component.
    // If focus moves to something inside (e.g., the scrollable webmodule-fade),
    // keep the preview open.
    if (!event.relatedTarget) {
      this.hide();
      return;
    }

    const target = event.relatedTarget as Node;
    if (!this.isTargetInNestedShadowDOM(target)) {
      this.hide();
    }
  };

  private handleFocus = () => {
    if (this.disabled) {
      return;
    }
    this.show();
  };

  private handleDocumentKeyDown = (event: KeyboardEvent) => {
    // Pressing escape when a preview is open should dismiss it
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.hide();
    }
  };

  private getShowDelay(): number {
    if (this.cachedShowDelay === null) {
      this.cachedShowDelay = parseDuration(getComputedStyle(this).getPropertyValue('--show-delay'));
    }
    return this.cachedShowDelay;
  }

  private getHideDelay(): number {
    if (this.cachedHideDelay === null) {
      this.cachedHideDelay = parseDuration(getComputedStyle(this).getPropertyValue('--hide-delay'));
    }
    return this.cachedHideDelay;
  }

  private handleMouseOver = () => {
    if (this.disabled) {
      return;
    }
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = window.setTimeout(() => this.show(), this.getShowDelay());
  };

  private handleMouseOut = () => {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = window.setTimeout(() => this.hide(), this.getHideDelay());
  };
}

// Set default animations
setDefaultAnimation('preview.show', {
  keyframes: [
    { opacity: 0, scale: 0.8 },
    { opacity: 1, scale: 1 }
  ],
  options: { duration: 150, easing: 'ease' }
});

setDefaultAnimation('preview.hide', {
  keyframes: [
    { opacity: 1, scale: 1 },
    { opacity: 0, scale: 0.8 }
  ],
  options: { duration: 150, easing: 'ease' }
});

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-preview': WebmodulePreview;
  }
}
